import { useMemo, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Globe, Pencil, X } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/marketing/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/marketing/components/ui/dialog";
import { cn } from "@/marketing/lib/utils";
import {
  CAMPAIGN_LIMITATION_KIND_LABEL,
  CAMPAIGN_REGION_OPTIONS,
  CampaignLimitationKind,
  type CampaignLimitation,
  type InfluencerCampaign,
  type UpdateInfluencerCampaignRequest,
} from "@/marketing/models/influencerCampaign";
import { influencerCampaignAPIClient } from "@/marketing/infrastructure/influencer/campaign/influencerCampaignAPIClient";
import { INFLUENCER_CAMPAIGN_KEYS } from "@/marketing/infrastructure/influencer/campaign/utils/keys";

interface CampaignLimitationsCardProps {
  campaign: InfluencerCampaign;
}

export function CampaignLimitationsCard({
  campaign,
}: CampaignLimitationsCardProps) {
  const regionLimitation = useMemo(
    () =>
      campaign.limitations?.find(
        (l) => l.kind === CampaignLimitationKind.Region,
      ),
    [campaign.limitations],
  );

  return (
    <div className="tw-rounded-lg tw-border tw-border-border tw-bg-card tw-p-4">
      <div className="tw-mb-2 tw-flex tw-items-center tw-justify-between">
        <div className="tw-text-[11px] tw-font-semibold tw-uppercase tw-tracking-wider tw-text-muted-foreground">
          Campaign limitations
        </div>
        <EditLimitationsDialog campaign={campaign} />
      </div>

      <LimitationRow
        label={CAMPAIGN_LIMITATION_KIND_LABEL[CampaignLimitationKind.Region]}
        values={regionLimitation?.values}
      />
    </div>
  );
}

function LimitationRow({
  label,
  values,
}: {
  label: string;
  values: string[] | undefined;
}) {
  return (
    <div className="tw-flex tw-items-start tw-gap-2 tw-py-1">
      <div className="tw-flex tw-w-16 tw-flex-shrink-0 tw-items-center tw-gap-1 tw-text-[11px] tw-text-muted-foreground">
        <Globe className="tw-h-3 tw-w-3" />
        {label}
      </div>
      {!values || values.length === 0 ? (
        <span className="tw-text-[12px] tw-text-muted-foreground">No restriction</span>
      ) : (
        <div className="tw-flex tw-flex-wrap tw-gap-1">
          {values.map((v) => (
            <span
              key={v}
              className="tw-rounded-full tw-border tw-border-blue-200 tw-bg-blue-50 tw-px-2 tw-py-0.5 tw-text-[11px] tw-font-medium tw-text-blue-700"
            >
              {v}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

function EditLimitationsDialog({ campaign }: { campaign: InfluencerCampaign }) {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [selectedRegions, setSelectedRegions] = useState<string[]>(() =>
    initialRegions(campaign.limitations),
  );

  const updateMutation = useMutation({
    mutationFn: (request: UpdateInfluencerCampaignRequest) =>
      influencerCampaignAPIClient.updateInfluencerCampaign(
        campaign.campaignId,
        request,
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: INFLUENCER_CAMPAIGN_KEYS.detail(campaign.campaignId),
      });
      toast.success("Campaign limitations updated");
      setOpen(false);
    },
    onError: (err: unknown) => toast.error(extractErrorMessage(err)),
  });

  const handleOpenChange = (next: boolean) => {
    if (next) setSelectedRegions(initialRegions(campaign.limitations));
    setOpen(next);
  };

  const toggleRegion = (code: string) => {
    setSelectedRegions((prev) =>
      prev.includes(code) ? prev.filter((c) => c !== code) : [...prev, code],
    );
  };

  const handleSave = () => {
    if (selectedRegions.length === 0) return;
    const limitations: CampaignLimitation[] = [
      { kind: CampaignLimitationKind.Region, values: selectedRegions },
    ];
    updateMutation.mutate({ limitations });
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className="tw-h-6 tw-gap-1 tw-px-2 tw-text-[11px] tw-text-muted-foreground"
        >
          <Pencil className="tw-h-3 tw-w-3" />
          Edit
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:tw-max-w-md">
        <DialogHeader>
          <DialogTitle>Campaign limitations</DialogTitle>
        </DialogHeader>

        <div className="tw-space-y-3">
          <div>
            <div className="tw-mb-2 tw-text-[12px] tw-font-medium">Regions</div>
            <p className="tw-mb-3 tw-text-[11px] tw-text-muted-foreground">
              Restrict this campaign to creators in selected regions. At least
              one region is required.
            </p>
            <div className="tw-grid tw-grid-cols-2 tw-gap-1.5">
              {CAMPAIGN_REGION_OPTIONS.map((opt) => {
                const checked = selectedRegions.includes(opt.code);
                return (
                  <button
                    key={opt.code}
                    type="button"
                    onClick={() => toggleRegion(opt.code)}
                    className={cn(
                      "tw-flex tw-items-center tw-justify-between tw-rounded-md tw-border tw-px-2.5 tw-py-1.5 tw-text-left tw-text-[12px] tw-transition-colors",
                      checked
                        ? "tw-border-blue-300 tw-bg-blue-50 tw-text-blue-800"
                        : "tw-border-border tw-bg-background tw-text-foreground hover:tw-bg-accent",
                    )}
                  >
                    <span>
                      <span className="tw-font-mono tw-text-[11px] tw-font-semibold">
                        {opt.code}
                      </span>
                      <span className="tw-ml-1.5 tw-text-[11px] tw-text-muted-foreground">
                        {opt.label}
                      </span>
                    </span>
                    {checked && <X className="tw-h-3 tw-w-3" />}
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setOpen(false)}
            disabled={updateMutation.isPending}
          >
            Cancel
          </Button>
          <Button
            size="sm"
            onClick={handleSave}
            disabled={updateMutation.isPending || selectedRegions.length === 0}
          >
            {updateMutation.isPending ? "Saving…" : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function initialRegions(
  limitations: CampaignLimitation[] | undefined,
): string[] {
  const region = limitations?.find(
    (l) => l.kind === CampaignLimitationKind.Region,
  );
  return region?.values ?? [];
}

function extractErrorMessage(err: unknown): string {
  if (typeof err === "object" && err !== null) {
    const response = (err as { response?: { data?: { message?: string } } })
      .response;
    if (response?.data?.message) return response.data.message;
    const message = (err as { message?: string }).message;
    if (message) return message;
  }
  return "Failed to save limitations";
}
